import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { db, functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { formatVND, formatWeekLabel, parseWeekId } from "@/lib/format";
import { sharePayslipPdf, buildPayslipMessage, payslipFileName } from "@/lib/share";
import { Toast } from "@/components/Toast";
import type { Registration, RegistrationShift } from "@/lib/types";

type Props = { weekId: string; hourlyRate: number };

type PayrollRow = {
  phone: string;
  name: string;
  totalHours: number;
  totalPay: number;
  payslipUrl: string | null;
};

export function PayrollPage({ weekId, hourlyRate }: Props) {
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sharingPhone, setSharingPhone] = useState<string | null>(null);

  const weekLabel = formatWeekLabel(parseWeekId(weekId));

  // Live payroll records (populated by the Cloud Function on Sprint 4)
  useEffect(() => {
    return onSnapshot(collection(db, "weeks", weekId, "payroll"), (snap) => {
      const docs: PayrollRow[] = snap.docs.map((d) => {
        const data = d.data();
        return {
          phone: data.phone as string,
          name: data.name as string,
          totalHours: data.totalHours as number,
          totalPay: data.totalPay as number,
          payslipUrl: (data.payslipUrl as string | null) ?? null,
        };
      });
      setRows(docs);
    });
  }, [weekId]);

  // Fallback: compute projected payroll from current registrations
  // (used when the Cloud Function hasn't run yet)
  useEffect(() => {
    if (rows.length > 0) return;
    (async () => {
      const regs = await getDocs(collection(db, "weeks", weekId, "registrations"));
      const acc = new Map<string, PayrollRow>();
      regs.forEach((d) => {
        const reg = d.data() as Registration;
        const hours = reg.shifts.reduce((s: number, x: RegistrationShift) => s + x.hours, 0);
        if (hours === 0) return;
        acc.set(reg.phone, {
          phone: reg.phone,
          name: reg.name,
          totalHours: hours,
          totalPay: hours * hourlyRate,
          payslipUrl: null,
        });
      });
      setRows(Array.from(acc.values()).sort((a, b) => b.totalPay - a.totalPay));
    })();
  }, [weekId, hourlyRate, rows.length]);

  const exportAll = async () => {
    if (busy) return;
    setBusy(true);
    setToast("Đang tạo payslip…");
    try {
      const fn = httpsCallable(functions, "calculateAndExportPayroll");
      await fn({ weekId });
      setToast("✅ Đã tạo payslip cho tất cả nhân viên");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast(`❌ Lỗi: ${msg.slice(0, 80)}`);
    } finally {
      setBusy(false);
    }
  };

  const shareToZalo = async (r: PayrollRow) => {
    if (!r.payslipUrl || sharingPhone) return;
    setSharingPhone(r.phone);
    setToast("Đang chuẩn bị PDF…");
    const result = await sharePayslipPdf({
      url: r.payslipUrl,
      fileName: payslipFileName(r.name, weekId),
      title: `Payslip ${r.name}`,
      text: buildPayslipMessage(r.name, weekLabel, r.totalHours, r.totalPay),
    });
    setSharingPhone(null);
    switch (result) {
      case "shared":
        setToast("✅ Đã chia sẻ payslip");
        break;
      case "cancelled":
        setToast(null);
        break;
      case "fallback":
        setToast("📄 Trình duyệt không hỗ trợ chia sẻ file — đã mở PDF, hãy lưu rồi gửi qua Zalo");
        break;
      case "error":
        setToast("❌ Không gửi được. Kiểm tra mạng/CORS rồi thử lại");
        break;
    }
  };

  const totalHours = rows.reduce((s, r) => s + r.totalHours, 0);
  const totalPay = rows.reduce((s, r) => s + r.totalPay, 0);

  return (
    <div>
      <div className="overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-pink-100">
        <div className="bg-pink-50/50 px-6 py-4 text-xs font-bold uppercase tracking-widest text-gray-500">
          Bảng lương tuần này
        </div>
        {rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">
            Chưa có ai đăng ký ca. Bảng lương sẽ xuất hiện sau khi duyệt.
          </div>
        ) : (
          <div className="divide-y divide-pink-50">
            {rows.map((r) => (
              <div key={r.phone} className="flex items-center justify-between px-6 py-4">
                <div>
                  <div className="font-bold text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">{r.totalHours} giờ</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-black text-pink-600 text-lg">{formatVND(r.totalPay)}</div>
                  <Link
                    to={`/manager/payslip/${weekId}/${r.phone}`}
                    className="rounded-full bg-pink-50 p-2 text-pink-600 transition hover:bg-pink-100"
                    title="Xem payslip"
                  >
                    📄
                  </Link>
                  {r.payslipUrl && (
                    <a
                      href={r.payslipUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-pink-50 p-2 text-pink-600 transition hover:bg-pink-100"
                      title="Tải payslip"
                    >
                      ⬇
                    </a>
                  )}
                  {r.payslipUrl && (
                    <button
                      onClick={() => shareToZalo(r)}
                      disabled={sharingPhone !== null}
                      className="rounded-full bg-blue-50 px-3 py-2 text-xs font-bold text-blue-600 transition hover:bg-blue-100 disabled:opacity-50"
                      title="Gửi payslip qua Zalo"
                    >
                      {sharingPhone === r.phone ? "…" : "Gửi Zalo"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between bg-pink-500 px-6 py-4 text-white">
          <span className="font-black text-lg">Tổng</span>
          <div className="text-right">
            <div className="text-xs opacity-80">{totalHours} giờ</div>
            <div className="font-black text-lg">{formatVND(totalPay)}</div>
          </div>
        </div>
      </div>

      <button
        onClick={exportAll}
        disabled={busy || rows.length === 0}
        className="btn-cta mt-4 w-full"
      >
        {busy ? "Đang tạo payslip…" : "🖨 Xuất tất cả payslip"}
      </button>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} variant="info" />}
    </div>
  );
}
